// src/components/Leaderboard.js
//
// The per-topic leaderboard at /leaderboard/:slug.
//
// ===========================================================================
// THE READ THAT HAD TO GO
// ===========================================================================
// The previous version did this, on every mount:
//
//     const userSnap = await getDocs(collection(db, "users"));
//     userSnap.docs.forEach(...)   // build firstName -> avatar map
//
// Three separate problems with it:
//
//   1. It's a `list` on /users. firestore.rules grants that to admins only,
//      so for the student whose score it is, this either threw a permission
//      error or — if the rules were ever loosened — quietly leaked the whole
//      user directory to a child's browser.
//
//   2. It's O(every user who has ever signed up), fetched to display at most
//      ten avatars. At a thousand users that's a thousand document reads per
//      page view, billed, to render ten emoji.
//
//   3. It keyed the map on firstName, so two children called Emma shared an
//      avatar and whichever loaded last won.
//
// None of it was necessary. UniversalQuizGame.saveToLeaderboard already
// writes `avatar` and `uid` onto the leaderboard document itself. The avatar
// is right there in the row.
//
// ===========================================================================
// THREE OTHER FIXES
// ===========================================================================
//
//   * ONE CHILD COULD FILL THE WHOLE BOARD. leaderBoard_{slug} is append-only
//     — a new document per play — so a kid who played eight times occupied
//     eight of the ten slots. Rows are now deduped by uid, keeping each
//     player's best.
//
//   * THE "THIS IS YOU" HIGHLIGHT NEVER FIRED. It compared
//     `entry.playerId === playerName`, where playerId is "Maya Rodriguez"
//     and playerName was set from `userSnap.data().firstName` — "Maya".
//     Never equal. It matches on uid now.
//
//   * WHOLE-COLLECTION FETCH. `getDocs(collection(...))` pulled every attempt
//     ever recorded for the topic and sorted in JS. Now orderBy + limit, so
//     Firestore does the work and sends 60 rows instead of all of them.
//     Single-field orderBy uses an automatic index — nothing to deploy.
//
// ===========================================================================
// ROUTE PROTECTION
// ===========================================================================
// This page shows student first names. The route must be wrapped in
// <PrivateRoute> — see README-round-2.md for the App.js change. The component
// also refuses to render rows to a signed-out visitor as a second line of
// defence, because a route is one careless edit away from being unwrapped
// again.

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { format } from "date-fns";
import "./Leaderboard.css";

const FALLBACK_AVATARS = ["🐱", "🦊", "🐻", "🐸", "🐯", "🦁", "🐨", "🐵", "🐶", "🦉"];
const MEDALS = ["🥇", "🥈", "🥉"];

// Pull more than we show, because deduping by player removes rows.
const FETCH_LIMIT = 60;
const SHOW_LIMIT = 10;

const titleCase = (s = "") =>
  String(s).replace(/[-_]/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

/** "Maya Rodriguez" -> "Maya R." Names on a shared screen stay partial. */
const shortName = (full = "") => {
  const parts = String(full).trim().split(/\s+/);
  if (parts.length < 2) return parts[0] || "Player";
  return `${parts[0]} ${parts[1][0]}.`;
};

export default function Leaderboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();

  const [entries, setEntries] = useState([]);
  const [uid, setUid] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || null);
      setAuthReady(true);
    });
    return () => unsub();
  }, [auth]);

  useEffect(() => {
    // Don't read anything until we know whether there's a signed-in user.
    if (!authReady) return;

    if (!uid) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const snap = await getDocs(
          query(
            collection(db, `leaderBoard_${slug}`),
            orderBy("totalScore", "desc"),
            limit(FETCH_LIMIT)
          )
        );

        // Best run per player. Rows arrive highest-first, so the first time we
        // see a uid is already that player's best and later ones are dropped.
        const bestByPlayer = new Map();
        snap.docs.forEach((d) => {
          const row = d.data();
          // Older rows predate the uid field; fall back to the name so they
          // still dedupe against themselves rather than each counting.
          const key = row.uid || row.playerId || d.id;
          if (!bestByPlayer.has(key)) {
            bestByPlayer.set(key, { id: d.id, ...row });
          }
        });

        if (!cancelled) {
          setEntries([...bestByPlayer.values()].slice(0, SHOW_LIMIT));
        }
      } catch (err) {
        console.error("[Leaderboard] load failed:", err);
        if (!cancelled) setError("We couldn't load this leaderboard right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, slug, uid, authReady]);

  if (!authReady || loading) {
    return (
      <div className="lb-shell">
        <p className="lb-message">Loading the leaderboard…</p>
      </div>
    );
  }

  if (!uid) {
    return (
      <div className="lb-shell">
        <div className="lb-message">
          <p>Please sign in to see the leaderboard.</p>
          <button className="lb-btn" onClick={() => navigate("/login")}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const topScore = entries[0]?.totalScore || 0;

  return (
    <div className="lb-shell">
      <header className="lb-header">
        <h1 className="lb-title">
          <span className="lb-trophy" aria-hidden="true">🏆</span>
          Top Scores
        </h1>
        <p className="lb-sub">{titleCase(slug)}</p>
      </header>

      {error ? (
        <p className="lb-message">{error}</p>
      ) : entries.length === 0 ? (
        <p className="lb-message">
          No scores yet — be the first to finish this one!
        </p>
      ) : (
        <ol className="lb-list">
          {entries.map((entry, i) => {
            const isMe = !!entry.uid && entry.uid === uid;
            const emoji = entry.avatar || FALLBACK_AVATARS[i % FALLBACK_AVATARS.length];
            const when = entry.timestamp?.toDate
              ? format(entry.timestamp.toDate(), "MMM d")
              : null;
            const barPct = topScore > 0 ? (entry.totalScore / topScore) * 100 : 0;

            return (
              <li key={entry.id} className={`lb-row ${isMe ? "me" : ""} ${i < 3 ? "top" : ""}`}>
                <span className="lb-rank">{i < 3 ? MEDALS[i] : i + 1}</span>

                <span className="lb-avatar" aria-hidden="true">
                  {/* A stored avatar may be an emoji or a Storage URL. */}
                  {/^https?:\/\//.test(emoji) ? (
                    <img src={emoji} alt="" />
                  ) : (
                    emoji
                  )}
                </span>

                <span className="lb-name">
                  {shortName(entry.playerId)}
                  {isMe && <span className="lb-you">YOU</span>}
                </span>

                <span className="lb-detail">
                  Quiz {entry.quizScore ?? 0} · Bonus {entry.bonusRoundScore ?? 0}
                  {when && ` · ${when}`}
                </span>

                <span className="lb-bar" aria-hidden="true">
                  <span className="lb-bar-fill" style={{ width: `${barPct}%` }} />
                </span>

                <span className="lb-points">{(entry.totalScore ?? 0).toLocaleString()}</span>
              </li>
            );
          })}
        </ol>
      )}

      <div className="lb-actions">
        <button className="lb-btn" onClick={() => navigate(-1)}>
          ↺ Play Again
        </button>
        <button className="lb-btn ghost" onClick={() => navigate("/dashboard")}>
          🏠 Home
        </button>
      </div>

      <p className="lb-note">Showing each player's best run.</p>
    </div>
  );
}
